import { useCallback, useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { MdmShell, Field, StatusBadge, ErrorAlert, s } from './_mdmKit.js';
import { Button } from '../../../../shared/components/index.js';
import { DataGrid } from '../../components/DataGrid.js';

// M05 §7/§8 (T063) — Rejection Reason Master. CRUD over the existing backend
// `@Controller('v1/master/rejection-reasons')`:
//   GET  /master/rejection-reasons?status → T063[]  (plain array)
//   POST /master/rejection-reasons (CreateRejectionReasonDto) · PUT /master/rejection-reasons/:id
//   PATCH /master/rejection-reasons/:id/deactivate
// UI-Audit C6: surfaces the spec fields requiresRemarks (bool) and sortOrder that
// already exist on the backend entity (no schema change).

interface Reason {
  readonly reasonId: string;
  readonly reasonCode: string;
  readonly reasonDescription: string;
  readonly requiresRemarks: boolean;
  readonly sortOrder: number;
  readonly recordStatusCode: string;
}

const STATUSES = ['ACTIVE', 'INACTIVE', 'ARCHIVED'] as const;

const EMPTY = { reasonCode: '', reasonDescription: '', requiresRemarks: false, sortOrder: '' };
type Form = typeof EMPTY;

export function RejectionReasonMasterPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<Reason[] | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<Form>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const qs = params.toString();
      const r = await apiRequest<Reason[]>(`/master/rejection-reasons${qs ? `?${qs}` : ''}`, { token });
      setRows(Array.isArray(r) ? r : []);
      setError(null);
    } catch (e) { setError((e as Error).message); setRows([]); }
  }, [token, statusFilter]);

  useEffect(() => { void load(); }, [load]);

  const set = (k: keyof Form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const beginCreate = () => { setForm(EMPTY); setEditingId(null); setFormError(null); setShowForm(true); };
  const beginEdit = (r: Reason) => {
    setForm({
      reasonCode: r.reasonCode, reasonDescription: r.reasonDescription,
      requiresRemarks: !!r.requiresRemarks, sortOrder: r.sortOrder == null ? '' : String(r.sortOrder),
    });
    setEditingId(r.reasonId); setFormError(null); setShowForm(true);
  };

  const onSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!form.reasonDescription.trim()) { setFormError(t('mdm.reason.descRequired')); return; }
    if (form.sortOrder.trim()) {
      const n = Number(form.sortOrder);
      if (Number.isNaN(n) || n < 0) { setFormError(t('mdm.reason.sortInvalid')); return; }
    }
    setSaving(true); setFormError(null);
    const sortOrder = form.sortOrder.trim() ? Number(form.sortOrder) : undefined;
    try {
      if (editingId) {
        await apiRequest(`/master/rejection-reasons/${encodeURIComponent(editingId)}`, {
          method: 'PUT', token,
          body: { reasonDescription: form.reasonDescription.trim(), requiresRemarks: form.requiresRemarks, sortOrder },
        });
      } else {
        await apiRequest('/master/rejection-reasons', {
          method: 'POST', token,
          body: {
            reasonCode: form.reasonCode.trim(), reasonDescription: form.reasonDescription.trim(),
            requiresRemarks: form.requiresRemarks, sortOrder,
          },
        });
      }
      setShowForm(false); setForm(EMPTY); setEditingId(null);
      await load();
    } catch (err) { setFormError((err as Error).message); }
    finally { setSaving(false); }
  }, [editingId, form, token, load]);

  const onDeactivate = useCallback(async (r: Reason) => {
    setError(null);
    try {
      await apiRequest(`/master/rejection-reasons/${encodeURIComponent(r.reasonId)}/deactivate`, { method: 'PATCH', token });
      await load();
    } catch (e) { setError((e as Error).message); }
  }, [token, load]);

  return (
    <MdmShell
      label="rejection-reason-master"
      title={t('mdm.reason.title')}
      description={t('mdm.reason.description')}
      actions={<Button type="button" variant="primary" data-testid="new-reason" onClick={beginCreate}>{t('mdm.reason.newReason')}</Button>}
    >
      <form aria-label="reason-filters" onSubmit={(e) => { e.preventDefault(); void load(); }}
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
        <Field id="rr-status" label={t('mdm.col.status')}>
          <select id="rr-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={s.input}>
            <option value="">{t('mdm.common.all')}</option>
            {STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </Field>
        <Button type="submit" variant="secondary" data-testid="apply-filters">{t('mdm.common.search')}</Button>
      </form>

      {showForm && (
        <form onSubmit={onSubmit} aria-label={editingId ? 'edit-reason-form' : 'create-reason-form'} style={s.card}>
          <h2 style={{ fontSize: 15, marginTop: 0 }}>{editingId ? t('mdm.reason.editTitle') : t('mdm.reason.newTitle')}</h2>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Field id="r-reasonCode" label={t('mdm.reason.reasonCode')}>
              <input id="r-reasonCode" value={form.reasonCode} onChange={set('reasonCode')} required disabled={!!editingId} style={s.input} />
            </Field>
            <Field id="r-reasonDescription" label={t('mdm.reason.descriptionReq')}>
              <input id="r-reasonDescription" value={form.reasonDescription} onChange={set('reasonDescription')} required style={s.input} />
            </Field>
            <Field id="r-sortOrder" label={t('mdm.reason.sortOrder')} hint={t('mdm.reason.sortHint')}>
              <input id="r-sortOrder" type="number" value={form.sortOrder} onChange={set('sortOrder')} style={s.input} />
            </Field>
            <Field id="r-requiresRemarks" label={t('mdm.reason.requiresRemarks')}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                <input id="r-requiresRemarks" type="checkbox" checked={form.requiresRemarks}
                       onChange={(e) => setForm((f) => ({ ...f, requiresRemarks: e.target.checked }))} />
                {t('mdm.reason.mandatoryRemarks')}
              </label>
            </Field>
          </div>
          <ErrorAlert message={formError} />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Button type="submit" variant="primary" data-testid="save-reason" disabled={saving}>
              {saving ? t('mdm.common.saving') : editingId ? t('mdm.common.saveChanges') : t('mdm.reason.createReason')}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>{t('common.cancel')}</Button>
          </div>
        </form>
      )}

      <ErrorAlert message={error} />
      {!rows ? (
        <p>{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="reasons-empty" style={{ color: s.muted }}>{t('mdm.reason.empty')}</p>
      ) : (
        <DataGrid<any>
          caption="reasons-table"
          rows={rows}
          getRowId={(r) => r.reasonId}
          rowTestId={(r) => `reason-row-${r.reasonId}`}
          columns={[
            { key: 'code', header: t('mdm.col.code'), value: (r) => r.reasonCode },
            { key: 'description', header: t('mdm.col.description'), value: (r) => r.reasonDescription },
            { key: 'requiresRemarks', header: t('mdm.reason.requiresRemarks'), value: (r) => (r.requiresRemarks ? t('mdm.common.yes') : t('mdm.common.no')) },
            { key: 'sortOrder', header: t('mdm.reason.sortOrder'), value: (r) => r.sortOrder ?? 0, numeric: true },
            { key: 'status', header: t('mdm.col.status'), value: (r) => r.recordStatusCode, render: (r) => <StatusBadge status={r.recordStatusCode} /> },
          ]}
          actionsHeader={t('mdm.col.actions')}
          actions={(r) => (
            <div style={{ display: 'flex', gap: 6 }}>
              <Button type="button" variant="secondary" data-testid={`edit-${r.reasonId}`} onClick={() => beginEdit(r)}>{t('mdm.common.edit')}</Button>
              {r.recordStatusCode === 'ACTIVE' && (
                <Button type="button" variant="danger" data-testid={`deactivate-${r.reasonId}`} onClick={() => onDeactivate(r)}>{t('mdm.common.deactivate')}</Button>
              )}
            </div>
          )}
        />
      )}
    </MdmShell>
  );
}
